import {Guide, useGuideFavoriteMutation} from '@blesk-frontend/api';
import {
  cn,
  getStepWithImage,
  Image,
  ImageRender,
  Star,
  StaticImage,
  Tooltip,
} from '@blesk-frontend/ui';
import {Link, useMatch} from '@tanstack/react-router';
import {MouseEvent, useMemo} from 'react';
import {toast} from 'sonner';

import {EmptyImage} from './EmptyImage';

interface GuideCardProps {
  guide: Guide;
}

export const GuideCard = ({guide}: GuideCardProps) => {
  const {mutateAsync} = useGuideFavoriteMutation();
  const match = useMatch({from: '/_app/collections/$collectionId/', shouldThrow: false});

  const imgStep = useMemo(() => getStepWithImage(guide.steps), [guide.steps]);

  const onClickFavorite = async (e: MouseEvent) => {
    e.preventDefault();
    return toast.promise(mutateAsync(guide.id), {
      loading: 'Сохранение...',
      error: 'При добавлении в избранное произошла ошибка',
      success: (data) => (data.isFavorite ? 'Добавлено в избранное' : 'Удалено из избранного'),
    });
  };

  return (
    <Link
      className="group relative flex flex-col text-text-primary"
      to="/guides/$guideId"
      params={{guideId: guide.id}}
      search={match ? {collectionId: match?.params.collectionId} : undefined}
      mask={{to: '/guides/$guideId', params: {guideId: guide.id}}}
    >
      {'_count' in guide && (
        <div className="absolute top-4 right-4 z-10" onClick={onClickFavorite}>
          <Tooltip
            message={
              guide._count.favorite ? <p>Удалить из избранного</p> : <p>Добавить в избранное</p>
            }
          >
            <button
              className={cn(
                'flex cursor-pointer text-attention-primary transition-transform hover:scale-110 active:scale-95',
                guide._count.favorite
                  ? 'text-attention-primary drop-shadow-[0_1px_0.5px_rgba(0,0,0,0.25)]'
                  : 'text-text-secondary',
              )}
              type="button"
            >
              <Star className="size-6" variant={guide._count.favorite ? 'fill' : 'duotone'} />
            </button>
          </Tooltip>
        </div>
      )}

      <div
        className="mb-2 overflow-hidden rounded-md border bg-bg-secondary p-2 transition-all group-hover:bg-bg-secondary-hover"
        style={{aspectRatio: '1.7'}}
      >
        {imgStep ? (
          imgStep?.payload.src.cropped ? (
            <StaticImage
              src={imgStep.payload.src.cropped}
              alt={imgStep.payload.element.text || ''}
              data-id="step-image"
            />
          ) : (
            <ImageRender
              initialScale={1}
              className="rounded-sm"
              step={imgStep.payload}
              zoomButtons={false}
            />
          )
        ) : (
          <EmptyImage />
        )}
      </div>
      <div className="line-clamp-2 shrink-0 px-2 text-paragraph-sm">{guide.name}</div>
      <div className="mt-1 shrink-0 px-2 text-paragraph-xs text-text-muted">
        {guide.user.fullName}
      </div>
    </Link>
  );
};

export const GuideCardSkeleton = () => {
  return (
    <div className="flex flex-col gap-2">
      <div className="h-[200px] w-[300px] rounded-md border bg-bg-secondary p-2">
        <div className="relative flex size-full items-center justify-center rounded-md bg-border-primary text-text-contrast">
          <Star className="absolute top-2 right-2 size-6" variant="fill" />
          <Image className="size-[72px]" />
        </div>
      </div>
      <div className="mx-2 h-5 w-2/3 shrink-0 rounded-md border bg-border-primary px-2" />
      <div className="mx-2 mt-1 h-4 w-1/3 shrink-0 rounded-md border bg-border-primary px-2" />
    </div>
  );
};
